import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { 
  ClipboardCheck,
  Plus,
  ArrowLeft,
  Calendar,
  Upload,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Eye,
  Sparkles,
  Home,
  DollarSign,
  Camera,
  MapPin,
  AlertCircle,
  Users
} from "lucide-react";
import AdminSidebar from "../components/layout/AdminSidebar";
import GovernanceNotice from "../components/GovernanceNotice";
import FileUploadZone from "../components/FileUploadZone";
import AIDocumentAnalysis from "../components/AIDocumentAnalysis";

const STATUS_OPTIONS = [
  { value: "submitted", label: "Submitted", color: "bg-blue-500/10 text-blue-600" },
  { value: "incomplete", label: "Incomplete", color: "bg-yellow-500/10 text-yellow-600" },
  { value: "under_review", label: "Under Review", color: "bg-purple-500/10 text-purple-600" },
  { value: "site_visit_scheduled", label: "Site Visit Scheduled", color: "bg-indigo-500/10 text-indigo-600" },
  { value: "revision_requested", label: "Revision Requested", color: "bg-orange-500/10 text-orange-600" },
  { value: "approved", label: "Approved", color: "bg-green-500/10 text-green-600" },
  { value: "approved_with_conditions", label: "Approved w/ Conditions", color: "bg-emerald-500/10 text-emerald-600" },
  { value: "denied", label: "Denied", color: "bg-red-500/10 text-red-600" }
];

const DRCPage = ({ isBoard = false }) => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [checklists, setChecklists] = useState({});
  const [depositAmounts, setDepositAmounts] = useState({});
  const [properties, setProperties] = useState([]);
  
  // New submission state
  const [createOpen, setCreateOpen] = useState(false);
  const [newSubmission, setNewSubmission] = useState({
    property_id: "",
    project_type: "",
    project_description: ""
  });
  
  // Detail/review state
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [uploadDocType, setUploadDocType] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  
  // Board actions
  const [actionType, setActionType] = useState("");
  const [actionNotes, setActionNotes] = useState("");
  const [siteVisitDate, setSiteVisitDate] = useState("");
  const [prescreenLoading, setPrescreenLoading] = useState(false);
  const [prescreenResult, setPrescreenResult] = useState(null);

  const handlePrescreen = async (submissionId) => {
    try {
      setPrescreenLoading(true);
      const res = await axios.post(`${API}/drc-prescreen/${submissionId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000
      });
      setPrescreenResult(res.data);
      toast.success("Pre-screening complete");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Pre-screening failed");
    } finally {
      setPrescreenLoading(false);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      
      const response = await axios.get(`${API}/drc?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(response.data);
    } catch (error) {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };

  const fetchChecklists = async () => {
    try {
      const response = await axios.get(`${API}/drc/checklists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChecklists(response.data.checklists);
      setDepositAmounts(response.data.deposit_amounts);
    } catch (error) {
      console.error("Failed to load checklists");
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
    } catch (error) {
      console.error("Failed to load properties");
    }
  };

  useEffect(() => {
    fetchSubmissions();
    fetchChecklists();
    fetchProperties();
  }, [token, statusFilter]);

  const handleCreateSubmission = async () => {
    if (!newSubmission.property_id || !newSubmission.project_type || !newSubmission.project_description) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const response = await axios.post(`${API}/drc`, newSubmission, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const submissionId = response.data.submission_id;
      
      // Upload files if any
      if (pendingFiles.length > 0) {
        const formData = new FormData();
        pendingFiles.forEach(f => formData.append("files", f.file));
        const cats = pendingFiles.map(f => f.category || "").join(",");
        if (cats.replace(/,/g, "")) formData.append("categories", cats);
        try {
          await axios.post(`${API}/file-uploads/drc/${submissionId}`, formData, {
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
          });
          toast.success(`Submission created with ${pendingFiles.length} file(s). Deposit: $${response.data.deposit_amount}`);
        } catch {
          toast.warning("Submission created but some files failed to upload");
        }
      } else {
        toast.success(`Submission created. Deposit required: $${response.data.deposit_amount}`);
      }
      
      setCreateOpen(false);
      setNewSubmission({ property_id: "", project_type: "", project_description: "" });
      setPendingFiles([]);
      fetchSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create submission");
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedSubmission || !uploadDocType || !uploadFile) {
      toast.error("Please select a document type and file");
      return;
    }

    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("document_type", uploadDocType);

    try {
      await axios.post(`${API}/drc/${selectedSubmission.submission_id}/upload-document`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      toast.success("Document uploaded");
      setUploadDocType("");
      setUploadFile(null);
      // Refresh submission details
      const response = await axios.get(`${API}/drc/${selectedSubmission.submission_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedSubmission(response.data);
      fetchSubmissions();
    } catch (error) {
      toast.error("Failed to upload document");
    }
  };

  const handleBoardAction = async (action) => {
    if (!selectedSubmission) return;

    try {
      let endpoint = "";
      let params = new URLSearchParams();
      
      switch (action) {
        case "approve":
          endpoint = `/drc/${selectedSubmission.submission_id}/approve`;
          if (actionNotes) params.append("conditions", actionNotes);
          params.append("generate_letter", "true");
          break;
        case "deny":
          if (!actionNotes) {
            toast.error("Please provide a reason for denial");
            return;
          }
          endpoint = `/drc/${selectedSubmission.submission_id}/deny`;
          params.append("reason", actionNotes);
          params.append("generate_letter", "true");
          break;
        case "revision":
          if (!actionNotes) {
            toast.error("Please provide revision notes");
            return;
          }
          endpoint = `/drc/${selectedSubmission.submission_id}/request-revision`;
          params.append("revision_notes", actionNotes);
          break;
        case "site_visit":
          if (!siteVisitDate) {
            toast.error("Please select a site visit date");
            return;
          }
          endpoint = `/drc/${selectedSubmission.submission_id}/schedule-site-visit`;
          params.append("visit_date", siteVisitDate);
          if (actionNotes) params.append("notes", actionNotes);
          break;
        default:
          return;
      }

      await axios.post(`${API}${endpoint}?${params.toString()}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Action completed: ${action}`);
      setActionType("");
      setActionNotes("");
      setSiteVisitDate("");
      setDetailsOpen(false);
      fetchSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    }
  };

  const getStatusBadge = (status) => {
    const config = STATUS_OPTIONS.find(s => s.value === status);
    return config ? (
      <Badge className={config.color}>{config.label}</Badge>
    ) : (
      <Badge variant="outline">{status}</Badge>
    );
  };

  const getChecklistProgress = (submission) => {
    const status = submission.checklist_status;
    if (!status) return 0;
    return status.required > 0 ? (status.completed / status.required) * 100 : 100;
  };

  const content = (
    <div className={isBoard ? "p-6 lg:p-8 max-w-7xl mx-auto" : "min-h-screen bg-background"}>
      {/* Header - only for non-board */}
      {!isBoard && (
        <header className="border-b bg-card/95 backdrop-blur-sm sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/owner")} className="text-muted-foreground hover:text-foreground transition-colors duration-200">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <ClipboardCheck className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <span className="font-serif text-lg font-semibold block">My DRC Submissions</span>
                  <span className="text-xs text-muted-foreground">Track your design submissions</span>
                </div>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Page Title for Board */}
      {isBoard && (
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <ClipboardCheck className="w-5 h-5 text-primary" />
          </div>
          <div>
            <span className="font-serif text-lg font-semibold block">Design Review Committee</span>
            <span className="text-xs text-muted-foreground">Review and approve architectural requests</span>
          </div>
        </div>
      )}

      <div className={isBoard ? "" : "max-w-7xl mx-auto px-4 md:px-8 py-8"}>
        {/* Stats for Board */}
        {isBoard && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
            <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.1s' }}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Pending Review</p>
                    <p className="text-2xl font-bold mt-1">{submissions.filter(s => ["submitted", "under_review"].includes(s.status)).length}</p>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-info/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Clock className="w-5 h-5 text-info" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.2s' }}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Site Visits</p>
                    <p className="text-2xl font-bold mt-1">{submissions.filter(s => s.status === "site_visit_scheduled").length}</p>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <MapPin className="w-5 h-5 text-purple-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.3s' }}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Approved</p>
                    <p className="text-2xl font-bold text-success mt-1">{submissions.filter(s => s.status?.includes("approved")).length}</p>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <CheckCircle className="w-5 h-5 text-success" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="opacity-0 animate-fade-in-up border-border/50 hover:border-border hover:shadow-floating transition-all duration-300 group" style={{ animationDelay: '0.4s' }}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Deposits Held</p>
                    <p className="text-2xl font-bold mt-1">${submissions.reduce((sum, s) => sum + (s.deposit_amount || 0), 0).toLocaleString()}</p>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <DollarSign className="w-5 h-5 text-success" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUS_OPTIONS.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button className="ml-auto" onClick={() => navigate(isBoard ? "/board/drc/submit" : "/owner/drc/submit")} data-testid="new-submission-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Submission
          </Button>
        </div>

        {/* Submissions List */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading submissions...</div>
        ) : submissions.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <ClipboardCheck className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No submissions found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {submissions.map((sub) => (
              <Card key={sub.submission_id} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg">{sub.project_type}</h3>
                        {getStatusBadge(sub.status)}
                      </div>
                      <p className="text-muted-foreground mb-3 line-clamp-2">{sub.project_description}</p>
                      
                      {/* Checklist Progress */}
                      {sub.checklist_status && (
                        <div className="mb-3">
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-muted-foreground">Documents</span>
                            <span>{sub.checklist_status.completed}/{sub.checklist_status.required} required</span>
                          </div>
                          <Progress value={getChecklistProgress(sub)} className="h-2" />
                        </div>
                      )}
                      
                      <div className="flex flex-wrap gap-4 text-sm">
                        {sub.property_info && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Home className="w-4 h-4" />
                            Lot {sub.property_info.lot_number}
                          </span>
                        )}
                        {sub.owner_info && (
                          <span className="text-muted-foreground">
                            👤 {sub.owner_info.first_name} {sub.owner_info.last_name}
                          </span>
                        )}
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <DollarSign className="w-4 h-4" />
                          ${sub.deposit_amount || 0} deposit
                        </span>
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          {new Date(sub.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    
                    <Button variant="outline" onClick={() => { setSelectedSubmission(sub); setDetailsOpen(true); }}>
                      <Eye className="w-4 h-4 mr-2" />
                      {isBoard ? "Review" : "View"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Details/Review Dialog */}
        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedSubmission?.project_type}</DialogTitle>
              <DialogDescription>
                {selectedSubmission?.property_info && `Lot ${selectedSubmission.property_info.lot_number}`}
              </DialogDescription>
            </DialogHeader>
            
            {selectedSubmission && (
              <Tabs defaultValue="details">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="documents">Documents</TabsTrigger>
                  {isBoard && <TabsTrigger value="actions">Actions</TabsTrigger>}
                </TabsList>
                
                <TabsContent value="details" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Status</Label>
                      <div className="mt-1">{getStatusBadge(selectedSubmission.status)}</div>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Deposit</Label>
                      <p className="font-medium">${selectedSubmission.deposit_amount || 0}</p>
                    </div>
                  </div>
                  {/* Auto-Assigned Reviewer */}
                  {selectedSubmission.assigned_reviewer_role && (
                    <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <Users className="w-4 h-4 text-blue-600" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-800">Auto-Assigned Reviewer</p>
                        <p className="text-xs text-blue-700 capitalize">{selectedSubmission.assigned_reviewer_role.replace('_', ' ')}</p>
                      </div>
                      <Badge variant="outline" className="text-blue-600 border-blue-300">Auto-assigned</Badge>
                    </div>
                  )}
                  <div>
                    <Label className="text-muted-foreground">Project Description</Label>
                    <p className="mt-1">{selectedSubmission.project_description}</p>
                  </div>
                  {selectedSubmission.conditions_of_approval && (
                    <div className="p-3 bg-green-50 rounded-lg">
                      <Label className="text-green-800">Conditions of Approval</Label>
                      <p className="mt-1 text-green-700">{selectedSubmission.conditions_of_approval}</p>
                    </div>
                  )}
                  {selectedSubmission.review_notes && (
                    <div className="p-3 bg-muted rounded-lg">
                      <Label>Review Notes</Label>
                      <p className="mt-1">{selectedSubmission.review_notes}</p>
                    </div>
                  )}
                  {/* AI Document Analysis Button */}
                  {isBoard && (
                    <div className="pt-2 space-y-3">
                      <AIDocumentAnalysis
                        context={`DRC Submission: ${selectedSubmission.project_type} - ${selectedSubmission.project_description?.substring(0, 200)}`}
                        analysisType="drc_review"
                        buttonText="Analyze Document with AI"
                      />
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => handlePrescreen(selectedSubmission.submission_id)}
                        disabled={prescreenLoading}
                        data-testid="drc-prescreen-btn"
                      >
                        {prescreenLoading ? (
                          <><Clock className="w-4 h-4 mr-2 animate-spin" /> Running Pre-Screen...</>
                        ) : (
                          <><Sparkles className="w-4 h-4 mr-2" /> AI Pre-Screen Submission</>
                        )}
                      </Button>
                      {prescreenResult && (
                        <div className="p-3 bg-violet-50 rounded-lg border border-violet-200 text-sm whitespace-pre-wrap" data-testid="prescreen-result">
                          <p className="font-medium text-violet-800 mb-2 flex items-center gap-1">
                            <Sparkles className="w-4 h-4" /> Pre-Screening Result
                          </p>
                          {prescreenResult.prescreen_result}
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="documents" className="space-y-4 mt-4">
                  {/* Document Checklist */}
                  <div className="space-y-2">
                    <Label>Required Documents</Label>
                    {checklists[selectedSubmission.project_type]?.map((doc, i) => {
                      const isSubmitted = selectedSubmission.submitted_documents?.some(d => d.document_type === doc.name);
                      return (
                        <div key={i} className={`flex items-center gap-3 p-2 rounded ${isSubmitted ? 'bg-green-50' : doc.required ? 'bg-orange-50' : 'bg-muted'}`}>
                          {isSubmitted ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <div className={`w-5 h-5 rounded border-2 ${doc.required ? 'border-orange-400' : 'border-gray-300'}`} />
                          )}
                          <div className="flex-1">
                            <p className="font-medium text-sm">{doc.name}</p>
                            <p className="text-xs text-muted-foreground">{doc.description}</p>
                          </div>
                          {doc.required && !isSubmitted && (
                            <Badge variant="outline" className="text-orange-600">Required</Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Upload Section */}
                  {!["approved", "approved_with_conditions", "denied"].includes(selectedSubmission.status) && (
                    <div className="p-4 border rounded-lg space-y-3">
                      <Label>Upload Document</Label>
                      <Select value={uploadDocType} onValueChange={setUploadDocType}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select document type" />
                        </SelectTrigger>
                        <SelectContent>
                          {checklists[selectedSubmission.project_type]?.map(doc => (
                            <SelectItem key={doc.name} value={doc.name}>{doc.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input type="file" onChange={(e) => setUploadFile(e.target.files[0])} />
                      <Button onClick={handleUploadDocument} disabled={!uploadDocType || !uploadFile}>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload
                      </Button>
                    </div>
                  )}
                </TabsContent>
                
                {isBoard && (
                  <TabsContent value="actions" className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Button variant="outline" className="h-20" onClick={() => setActionType("site_visit")}>
                        <div className="text-center">
                          <MapPin className="w-6 h-6 mx-auto mb-1" />
                          <span>Schedule Site Visit</span>
                        </div>
                      </Button>
                      <Button variant="outline" className="h-20" onClick={() => setActionType("revision")}>
                        <div className="text-center">
                          <FileText className="w-6 h-6 mx-auto mb-1" />
                          <span>Request Revision</span>
                        </div>
                      </Button>
                      <Button variant="outline" className="h-20 text-green-600 hover:text-green-700" onClick={() => setActionType("approve")}>
                        <div className="text-center">
                          <CheckCircle className="w-6 h-6 mx-auto mb-1" />
                          <span>Approve</span>
                        </div>
                      </Button>
                      <Button variant="outline" className="h-20 text-red-600 hover:text-red-700" onClick={() => setActionType("deny")}>
                        <div className="text-center">
                          <XCircle className="w-6 h-6 mx-auto mb-1" />
                          <span>Deny</span>
                        </div>
                      </Button>
                    </div>
                    
                    {actionType && (
                      <div className="p-4 border rounded-lg space-y-3">
                        <Label className="font-medium">
                          {actionType === "approve" && "Approve Submission"}
                          {actionType === "deny" && "Deny Submission"}
                          {actionType === "revision" && "Request Revision"}
                          {actionType === "site_visit" && "Schedule Site Visit"}
                        </Label>
                        
                        {actionType === "site_visit" && (
                          <Input
                            type="datetime-local"
                            value={siteVisitDate}
                            onChange={(e) => setSiteVisitDate(e.target.value)}
                          />
                        )}
                        
                        <Textarea
                          value={actionNotes}
                          onChange={(e) => setActionNotes(e.target.value)}
                          placeholder={
                            actionType === "approve" ? "Conditions (optional)..." :
                            actionType === "deny" ? "Reason for denial (required)..." :
                            actionType === "revision" ? "Required revisions..." :
                            "Notes..."
                          }
                          rows={3}
                        />
                        
                        <div className="flex gap-2">
                          <Button variant="outline" onClick={() => setActionType("")}>Cancel</Button>
                          <Button onClick={() => handleBoardAction(actionType)}>
                            Confirm {actionType.replace("_", " ")}
                          </Button>
                        </div>
                      </div>
                    )}
                  </TabsContent>
                )}
              </Tabs>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );

  if (isBoard) {
    return <AdminSidebar>{content}</AdminSidebar>;
  }
  return content;
};

export default DRCPage;
